// Shared types for the survey→Excel export engine, importable from both the
// web app (via the @excel-export alias) and the generate-survey-export edge
// function. Everything here is pure — no browser or Deno APIs.

/** Base field properties shared by all field types */
interface BaseField {
  id: string;
  title: string;
  ref_id: string;
}

/** Optional metadata attached to every leaf question */
interface LeafMetadata {
  confidence?: number | null;
  evidence?: string[];
  notes?: string | null;
  required?: boolean;
}

/** A "node" field that contains nested child fields (e.g., "Interior", "Roof") */
export interface NodeField extends BaseField {
  type: "node";
  fields: AttributeField[];
}

/** A field with a simple value: string, number, boolean, or null */
export interface InputField extends BaseField, LeafMetadata {
  type: "input";
  value: string | number | boolean | null;
}

/** A select field with a string, boolean, number, or null value */
export interface SelectField extends BaseField, LeafMetadata {
  type: "select";
  value: string | number | boolean | null;
}

/** A multi-select field containing an array of selected string values */
export interface MultiSelectField extends BaseField, LeafMetadata {
  type: "multiSelect";
  value: string[] | null;
}

/** A photos field containing an array of image URLs */
export interface PhotosField extends BaseField, LeafMetadata {
  type: "photos";
  value: string[] | null;
}

/** A text area field containing HTML content */
export interface AreaField extends BaseField, LeafMetadata {
  type: "area";
  value: string | null;
}

/** Union of all possible attribute field types */
export type AttributeField =
  | NodeField
  | InputField
  | SelectField
  | MultiSelectField
  | PhotosField
  | AreaField;

/**
 * Per-option cell mapping authored in robotic-imaging-web's "Map options to
 * cells" dialog. A switch's two states arrive as reserved options
 * "true"/"false" (the boolean answer stringifies to match).
 */
export interface ExcelOptionCell {
  option: string;
  cell: string;
  sheet?: string | null;
  /** Written when the option is selected; defaults to the option label. */
  value?: string | null;
}

/**
 * Shape of a field in the attribute_templates schema_definition JSON.
 * Each field may have an `options` array (for select types) and
 * nested `fields` (for node types).
 */
export interface TemplateField {
  id: string;
  type: string;
  title: string;
  options?: string[] | null;
  fields?: TemplateField[];
  hint?: string | null;
  required?: boolean;
  /** A1-style cell (e.g. "D20") this answer maps to in an Excel template export. */
  excelTemplateCell?: string | null;
  /** Optional worksheet for excelTemplateCell; defaults to the workbook's first sheet. */
  excelTemplateSheet?: string | null;
  /** Additional cells this answer is stamped into (e.g. summary-sheet headers). */
  excelTemplateCells?: Array<{ cell: string; sheet?: string | null }> | null;
  /** When true, excelOptionCells replaces the single-cell mapping above. */
  excelUsePerOption?: boolean | null;
  excelOptionCells?: ExcelOptionCell[] | null;
}

// Type-only import — erased at runtime, so the exportCore ↔ types cycle is safe.
import type { ExcelCellMapping } from "./exportCore.ts";

/**
 * Parses an attribute_templates row into template fields + Excel mappings +
 * the template's default worksheet (cells mapped without an explicit sheet
 * resolve to it; null on rows synced before the excel_default_sheet column).
 * schema_definition is normally the field array; an object shape
 * { fields, excelCellMappings } (the web form doc layout) is accepted too, and
 * the excel_cell_mappings column wins over inline mappings when present.
 * Shared so the browser hook and the edge function parse identically.
 */
export function parseTemplateRow(row: {
  schema_definition: unknown;
  excel_cell_mappings?: unknown;
  excel_default_sheet?: unknown;
}): {
  templateFields: TemplateField[];
  excelCellMappings: ExcelCellMapping[];
  excelDefaultSheet: string | null;
} {
  const raw =
    typeof row.schema_definition === "string"
      ? JSON.parse(row.schema_definition)
      : row.schema_definition;
  const fields: TemplateField[] = Array.isArray(raw) ? raw : (raw?.fields ?? []);
  const inlineMappings: ExcelCellMapping[] = Array.isArray(raw)
    ? []
    : (raw?.excelCellMappings ?? []);
  const columnMappings: ExcelCellMapping[] =
    typeof row.excel_cell_mappings === "string"
      ? JSON.parse(row.excel_cell_mappings)
      : ((row.excel_cell_mappings as ExcelCellMapping[] | null | undefined) ?? []);
  return {
    templateFields: fields,
    excelCellMappings: columnMappings.length > 0 ? columnMappings : inlineMappings,
    excelDefaultSheet:
      typeof row.excel_default_sheet === "string" && row.excel_default_sheet
        ? row.excel_default_sheet
        : null,
  };
}
